import { INote } from '../INote';
import { IAttachment } from './IAttachment';
import { IUploadingFile } from './FileUploader';
import { NoteAttachment } from './NoteAttachment';
import { createUseStyles } from 'react-jss';
import { DeleteAttachment } from './DeleteAttachment';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { INoteTarget } from '../INoteTarget';
import { useEffect, useRef, useState } from 'react';
import { usePrevious } from '../../../utils/usePrevious';
import { CareMapNoteTracker } from '../../model/CareMapNoteTracker';

const useStyles = createUseStyles({
    list: {
        gap: 10,
        display: 'flex',
        overflowX: 'auto',
        overflowY: 'hidden'
    }
});

interface IAttachmentListProps {
    note: INote;
    attachments: IAttachment[];
    canEdit?: boolean;
    marginBottom?: number;
    noteTarget?: INoteTarget;
    uploadingAttachments?: IUploadingFile[],
    noteChanged?: (updatedNote: INote) => void;
};

export const AttachmentList:React.FC<IAttachmentListProps> = ({
    note,
    noteTarget,
    canEdit = false,
    marginBottom,
    attachments,
    uploadingAttachments,
    noteChanged = () => {}
}) => {
    const classes = useStyles();
    const tracker = useInstance(CareMapNoteTracker);
    const listRef = useRef<HTMLDivElement>(null);

    const [uploadCount, setUploadCount] = useState(uploadingAttachments?.length);
    const prevUploadCount = usePrevious(uploadCount);

    // doesn't get called when canEdit = false as the DeleteAttachment component won't exist
    const deleteAttachment = async ({ url }: { url: string; }) => {
        // NOTE: if the attachment being removed is the appt recording attachment,
        // we are leaving the recording metadata, but this is what zannah has requested

        noteChanged({
            ...note,
            attachments: note.attachments.filter(a => a.url !== url)
        });

        tracker.onFileRemoved(noteTarget!, url);
    };

    useEffect(() => {
        setUploadCount(uploadingAttachments?.length);
    }, [uploadingAttachments?.length]);

    // if uploading or just did upload, scroll to the end of the list so the user can see it
    useEffect(() => {
        if (uploadingAttachments?.length || (!uploadingAttachments?.length && prevUploadCount)) {
            listRef.current?.scrollTo({left: listRef.current?.scrollWidth});
        }
    }, [listRef, uploadingAttachments, prevUploadCount, uploadCount]);

    const style = (!attachments.length && !uploadingAttachments?.length ? undefined : { marginBottom });

    return (
        <div ref={listRef} className={classes.list} data-type="attachment-list" style={style}>
            {attachments.map(attachment => (
                <div key={attachment.url} style={{display: 'flex', flexDirection: 'column'}}>
                    <NoteAttachment {...attachment} />

                    {canEdit && (
                        <DeleteAttachment onDelete={() => deleteAttachment(attachment)} />
                    )}
                </div>
            ))}

            {uploadingAttachments?.map((file, i) => (
                <NoteAttachment key={i} {...{url: '', ...file, isUploading: true}} />
            ))}
        </div>
    );
};
